import _ from "lodash";
import {
	AdjustmentsFrequency,
	ComputationFrequency,
	ContractDetailType,
	ContractPeriodSettlementCategories,
	ContractPeriodType,
	CurtailabilityForward,
	NegativePrice,
	SettlementType,
} from "../../../requests_cm/gecoContractsService/types";
import {
	getContractPeriodFromId,
	getContractPeriodIndexFromId,
	getSettlementIndexFromId,
} from "./common";

export interface FormikNpqrs {
	n: number | undefined;
	p: number | undefined;
	q: number | undefined;
	r: number | undefined;
	s: number | undefined;
}

export interface InvoicingPeriodFormikData {
	invoice_decomposition: boolean;
	has_adjustments: boolean;
	adjustment_frequency?: AdjustmentsFrequency;
	computation_frequency?: ComputationFrequency;
}

export const npqrsToFormik = (npqrs: number[] | undefined): FormikNpqrs => {
	let n = undefined;
	let p = undefined;
	let q = undefined;
	let r = undefined;
	let s = undefined;
	if (npqrs) {
		n = npqrs[0];
		p = npqrs[1];
		q = npqrs[2];
		r = npqrs[3];
		s = npqrs[4];
	}
	return { n, p, q, r, s };
};

export const settlementFormikToContractMapper = (
	settlementFormik: SettlementType,
	initialContract: ContractDetailType,
	contractPeriodId: number,
	settlementCategory: ContractPeriodSettlementCategories
): ContractDetailType => {
	const updatedContract = _.cloneDeep(initialContract) as ContractDetailType;
	const contractPeriodIndex = getContractPeriodIndexFromId(
		updatedContract.contract_periods,
		contractPeriodId
	);
	const contractPeriod = getContractPeriodFromId(
		updatedContract.contract_periods,
		contractPeriodId
	) as ContractPeriodType;
	const newSettlement = _.omitBy(
		settlementFormik,
		(value, key) =>
			(key === "cap_floor" && value?.payout_method === "None") ||
			key === "cap_floor_intraday" // TODO GRES-11858 add the "&& value?.payout_method === "None""  once cap_floor_intraday is ready on the GECO side.
	);
	//TODO GRES-11858 remove omit here once cap_floor_intraday is ready on the GECO side.
	const settlements = contractPeriod[settlementCategory].settlements.map(
		(settlement: SettlementType) =>
			_.omit(settlement, ["cap_floor_intraday"])
	);
	const settlementIndex = getSettlementIndexFromId(
		settlements,
		settlementFormik.id
	);
	updatedContract.contract_periods[contractPeriodIndex][
		settlementCategory
	].settlements[settlementIndex] = newSettlement;

	return updatedContract;
};

export const negativePriceConditionFormikToContractMapper = (
	negativePriceFormik: NegativePrice,
	initialContract: ContractDetailType,
	contractPeriodId: number
): ContractDetailType => {
	const updatedContract = _.cloneDeep(initialContract) as ContractDetailType;
	const contractPeriodIndex = getContractPeriodIndexFromId(
		updatedContract.contract_periods,
		contractPeriodId
	);
	const contractPeriod = getContractPeriodFromId(
		updatedContract.contract_periods,
		contractPeriodId
	) as ContractPeriodType;

	contractPeriod.negative_price = negativePriceFormik;

	updatedContract.contract_periods[contractPeriodIndex] = contractPeriod;

	return updatedContract;
};

export const curtailabilityForwardFormikToContractMapper = (
	curtailabilityForwardFormik: CurtailabilityForward,
	initialContract: ContractDetailType,
	contractPeriodId: number
): ContractDetailType => {
	const updatedContract = _.cloneDeep(initialContract) as ContractDetailType;

	const contractPeriodIndex = getContractPeriodIndexFromId(
		updatedContract.contract_periods,
		contractPeriodId
	);
	const contractPeriod = getContractPeriodFromId(
		updatedContract.contract_periods,
		contractPeriodId
	) as ContractPeriodType;

	contractPeriod.curtailability = curtailabilityForwardFormik;

	updatedContract.contract_periods[contractPeriodIndex] = contractPeriod;

	return updatedContract;
};

export const clearNegativePriceCondition = (
	initialContract: ContractDetailType,
	contractPeriodId: number,
	settlementCategory: ContractPeriodSettlementCategories
): ContractDetailType => {
	const updatedContract = _.cloneDeep(initialContract) as ContractDetailType;
	const contractPeriodIndex = getContractPeriodIndexFromId(
		updatedContract.contract_periods,
		contractPeriodId
	);
	const contractPeriod = getContractPeriodFromId(
		updatedContract.contract_periods,
		contractPeriodId
	) as ContractPeriodType;

	delete contractPeriod.negative_price;

	const settlements = contractPeriod[settlementCategory].settlements;
	const updatedSettlements = (settlements || []).map(
		(singleSettlement: SettlementType) => ({
			...singleSettlement,
			condition_neg_price: false,
		})
	);

	updatedContract.contract_periods[contractPeriodIndex][
		settlementCategory
	].settlements = updatedSettlements;

	updatedContract.contract_periods[contractPeriodIndex] = contractPeriod;

	return updatedContract;
};

export const clearCurtailabilityCondition = (
	initialContract: ContractDetailType,
	contractPeriodId: number
): ContractDetailType => {
	const updatedContract = _.cloneDeep(initialContract) as ContractDetailType;
	const contractPeriodIndex = getContractPeriodIndexFromId(
		updatedContract.contract_periods,
		contractPeriodId
	);
	const contractPeriod = getContractPeriodFromId(
		updatedContract.contract_periods,
		contractPeriodId
	) as ContractPeriodType;

	delete contractPeriod.curtailability;

	updatedContract.contract_periods[contractPeriodIndex] = contractPeriod;

	return updatedContract;
};

export const invoicingPeriodFormikToContractMapper = (
	invoicingPeriodFormik: InvoicingPeriodFormikData,
	initialContract: ContractDetailType,
	contractPeriodId: number,
): ContractDetailType => {
	const updatedContract = _.cloneDeep(initialContract) as ContractDetailType;
	const contractPeriodIndex = getContractPeriodIndexFromId(
		updatedContract.contract_periods,
		contractPeriodId
	);
	const contractPeriod = getContractPeriodFromId(
		updatedContract.contract_periods,
		contractPeriodId
	) as ContractPeriodType;

	contractPeriod.invoicing = {
		...contractPeriod.invoicing,
		invoice_decomposition: !!invoicingPeriodFormik.invoice_decomposition,
		has_adjustments: !!invoicingPeriodFormik.has_adjustments,
		adjustment_frequency: (invoicingPeriodFormik.adjustment_frequency ??
			contractPeriod.invoicing?.adjustment_frequency),
		monthly_settlement: {
			...contractPeriod.invoicing?.monthly_settlement,
			computation_frequency:
				invoicingPeriodFormik.computation_frequency ??
				contractPeriod.invoicing?.monthly_settlement
					?.computation_frequency,
		},
	};

	updatedContract.contract_periods[contractPeriodIndex] = contractPeriod;

	return updatedContract;
};
